import '../Bonus.scss';
import { useEffect } from 'react';
import { Card, CardBody, Table, UncontrolledTooltip } from 'reactstrap';
import Icon from '@mdi/react';
import { mdiInformationOutline } from '@mdi/js';
import { useQuery } from '@tanstack/react-query';
import { getCurrentQuarter } from '../../../utils/date.util';
import { fetchBonusDetails } from '../../../store/apis/bonusService';
import { toFixedTwoDecimal } from '../../../utils/number.util';
import quaterly from '../../../assets/images/bonus/quaterly.png';
import hand from '../../../assets/images/bonus/hand.png';

function BonusSummary({ formData }) {
  const currentQuarter = getCurrentQuarter();

  const {
    data: fetchedSummary,
    isLoading: isSummaryLoading,
    isError: isSummaryError,
    refetch: refetchDetails,
  } = useQuery({
    queryKey: ['fetchedSummary', formData],
    queryFn: () => fetchBonusDetails({ type: 'SUMMARY', ...formData }),
    enabled: !!formData?.empCode,
  });

  useEffect(() => {
    if (formData?.empCode && formData?.period) {
      refetchDetails();
    }
  }, [formData.empCode, refetchDetails, formData?.period]);

  const noSummaryAvailable =
    !isSummaryLoading &&
    !isSummaryError &&
    (!fetchedSummary || fetchedSummary?.length === 0);
  // Calculate totals
  const totalTarget = fetchedSummary?.reduce(
    (sum, item) => sum + (parseFloat(item?.['Total Eligibility']) || 0),
    0,
  );
  const totalTotalActual = fetchedSummary?.reduce(
    (sum, item) => sum + (parseFloat(item.TotalActual) || 0),
    0,
  );

  return (
    <div>
      <div className="mb-2 quaterly-header d-flex flex-row align-items-center gap-2">
        <img src={quaterly} alt="quaterly" />
        <span className="fs-6 fw-semibold">Quarterly</span>
      </div>
      <Card className="quarterly-card">
        <CardBody className="p-0">
          <div className="quarterly-header-title px-3 py-1">
            <div className="me-2 bonus-detail-title">
              Current Fiscal Year Summary (No. of Months Basic Salary)
            </div>
            <div>
              <span id="quarterly-info-icon">
                <Icon path={mdiInformationOutline} size={0.7} />
              </span>
              <UncontrolledTooltip placement="top" target="quarterly-info-icon">
                This shows the quarterly bonus summary for the current fiscal
                year. Values are calculated as multiples of basic salary.
              </UncontrolledTooltip>
            </div>
          </div>
          <div
            className="table-responsive table-bottom-border-radius"
            style={{ width: '100%' }}
          >
            <Table className="custom-table table-nowrap mb-0 quarter-summary-table w-100">
              <thead>
                <tr>
                  <th
                    className="text-center fw-semibold py-1 quarter-column"
                    title="No. of months basic salary actual payout quarterly based on the results and score"
                  >
                    Quarter
                  </th>
                  <th
                    className="text-center fw-semibold py-1 quarter-column"
                    title="No. of months basic salary eligibility for Quarter portion target bonus"
                  >
                    Qtly. Payout Multiplier
                  </th>
                  <th
                    className="text-center fw-semibold py-1 quarter-column"
                    title="No. of months basic salary eligibility for Cumulative portion target bonus"
                  >
                    Cum. Payout Multiplier
                  </th>
                  <th
                    className="text-center fw-semibold py-1 quarter-column"
                    title="Total no. of months basic salary eligiblity for quarterly (sum of quarter portion & cumulative portion)"
                  >
                    Target
                  </th>
                  <th
                    className="text-center fw-semibold py-1 quarter-column"
                    title="Actual no. of months basic salary bonus as per the result and score"
                  >
                    Result
                  </th>
                </tr>
              </thead>
              <tbody>
                {isSummaryLoading && (
                  <tr>
                    <td colSpan={5}>Loading...</td>
                  </tr>
                )}
                {isSummaryError && (
                  <tr>
                    <td colSpan={5}>Error loading summary</td>
                  </tr>
                )}
                {noSummaryAvailable && (
                  <tr>
                    <td colSpan={5} className="text-danger fw-bold">
                      No data available
                    </td>
                  </tr>
                )}
                {!isSummaryLoading &&
                  !isSummaryError &&
                  fetchedSummary?.length > 0 &&
                  fetchedSummary.map(item => (
                    <tr
                      key={item?.Qtr}
                      className={`${
                        item.Qtr === currentQuarter
                          ? 'current-quarter-border'
                          : ''
                      } table-row-tall`}
                    >
                      <td
                        className={`quarter-td  ${item.Qtr === currentQuarter ? 'quarter-indicator-row' : ''} align-middle`}
                      >
                        {item.Qtr === currentQuarter && (
                          <img
                            src={hand}
                            alt="hand"
                            className="quarter-hand-icon"
                          />
                        )}
                        <span>{`Q${item.Qtr}`}</span>
                      </td>
                      <td className="text-center quarter-column align-middle">
                        {toFixedTwoDecimal(item?.Eligibility_Qtr)}
                      </td>
                      <td className="text-center quarter-column align-middle">
                        {toFixedTwoDecimal(item?.Eligibility_QtrCumulative)}
                      </td>
                      <td className="highlighted-payout quarter-column align-middle">
                        {/* {toFixedTwoDecimal(item?.['Total Eligibility'])}hi */}
                        {parseFloat(item?.['Total Eligibility']).toLocaleString(
                          undefined,
                          {
                            minimumFractionDigits: 3,
                            maximumFractionDigits: 3,
                          },
                        )}
                      </td>
                      <td className="py-1 highlighted-payout quarter-column align-middle">
                        {/* {toFixedTwoDecimal(item?.TotalActual)} */}
                        {parseFloat(item?.TotalActual).toLocaleString(
                          undefined,
                          {
                            minimumFractionDigits: 3,
                            maximumFractionDigits: 3,
                          },
                        )}
                      </td>
                    </tr>
                  ))}
                <tr>
                  <td className="quarterly-total-payout-row py-1" colSpan={2}>
                    {}
                  </td>
                  <td className="quarterly-total-payout quarterly-total-payout-row py-1 quarter-column">
                    Quarterly Bonus Payout
                  </td>
                  <td className="quarterly-total-payout quarterly-total-payout-row py-1">
                    {(parseFloat(totalTarget) || 0).toLocaleString(undefined, {
                      minimumFractionDigits: 3,
                      maximumFractionDigits: 3,
                    })}
                  </td>
                  <td className="quarterly-total-payout quarterly-total-payout-row py-1">
                    {parseFloat(totalTotalActual).toLocaleString(undefined, {
                      minimumFractionDigits: 3,
                      maximumFractionDigits: 3,
                    })}
                  </td>
                </tr>
              </tbody>
            </Table>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

export default BonusSummary;
